import {
  ACTION_COLUMN_TITLE,
  isActionColumn,
} from "../app-table/actionColumn"
import { DEFAULT_COLUMN_WIDTH, DEFAULT_VIRTUAL_SCROLL_Y } from "./constants"
import { ColumnGroup, FreezableColumn, LeafColumn, ReportColumn } from "./types"

export const buildColumnSchema = (
  columns: ReportColumn[],
  freezeGroupedColumnsByLeaf = false
) => {
  const groups: ColumnGroup[] = []
  const leaves: LeafColumn[] = []
  const freezableColumns: FreezableColumn[] = []

  columns.forEach((column, index) => {
    const columnKey = getColumnKey(column, index)
    const columnTitle = getColumnLabel(column)

    if (column.children?.length) {
      const groupLeaves = column.children.map((child, childIndex) => {
        const leaf = {
          key: getColumnKey(child, childIndex, columnKey),
          title: getColumnLabel(child),
          column: child,
          groupKey: columnKey,
        }
        leaves.push(leaf)
        return leaf
      })

      groups.push({
        key: columnKey,
        title: columnTitle,
        leaves: groupLeaves,
      })

      if (freezeGroupedColumnsByLeaf && column.freezable !== false) {
        freezableColumns.push(
          ...groupLeaves.map((leaf) => ({
            key: leaf.key,
            title: leaf.title,
            leafKeys: [leaf.key],
          }))
        )
      } else if (!freezeGroupedColumnsByLeaf && groupLeaves.length <= 1) {
        freezableColumns.push({
          key: columnKey,
          title: columnTitle,
          leafKeys: groupLeaves.map((leaf) => leaf.key),
        })
      }
      return
    }

    const leaf = {
      key: columnKey,
      title: columnTitle,
      column,
    }
    leaves.push(leaf)
    groups.push({
      key: columnKey,
      title: columnTitle,
      leaves: [leaf],
    })
    freezableColumns.push({
      key: columnKey,
      title: columnTitle,
      leafKeys: [columnKey],
    })
  })

  return {
    groups,
    leaves,
    freezableColumns,
  }
}

export const filterColumns = (
  columns: ReportColumn[],
  options: {
    hiddenColumnKeySet: Set<string>
    frozenLeafKeys: string[]
  }
): ReportColumn[] => {
  const { hiddenColumnKeySet, frozenLeafKeys } = options
  const frozenLeafKeySet = new Set(frozenLeafKeys)
  const frozenLeafOrder = new Map(
    frozenLeafKeys.map((key, index) => [key, index])
  )

  const visibleColumns = columns
    .flatMap((column, index) => {
      const columnKey = getColumnKey(column, index)

      if (column.children?.length) {
        const visibleChildren = column.children
          .map((child, childIndex) => {
            const childKey = getColumnKey(child, childIndex, columnKey)
            if (hiddenColumnKeySet.has(childKey)) return null

            return decorateColumn(child, {
              key: childKey,
              isFrozen: frozenLeafKeySet.has(childKey),
              isLastFrozen: false,
            })
          })
          .filter(Boolean) as ReportColumn[]

        if (!visibleChildren.length) return []

        const frozenChildren = visibleChildren
          .filter((child) => child.fixed)
          .sort(
            (a, b) =>
              (frozenLeafOrder.get(String(a.key)) ?? Number.MAX_SAFE_INTEGER) -
              (frozenLeafOrder.get(String(b.key)) ?? Number.MAX_SAFE_INTEGER)
          )
        const scrollingChildren = visibleChildren.filter(
          (child) => !child.fixed
        )
        const buildGroup = (
          children: ReportColumn[],
          key: string,
          fixed?: "left"
        ): ReportColumn => ({
          ...column,
          key,
          title: fixed ? null : column.title,
          fixed,
          width: children.reduce(
            (total, child) =>
              total + Number(child.width || DEFAULT_COLUMN_WIDTH),
            0
          ),
          className: column.className,
          children,
        })

        return [
          ...frozenChildren.map((child) =>
            buildGroup(
              [child],
              `${columnKey}__frozen__${String(child.key)}`,
              "left"
            )
          ),
          ...(scrollingChildren.length
            ? [buildGroup(scrollingChildren, columnKey)]
            : []),
        ]
      }

      if (hiddenColumnKeySet.has(columnKey)) return []

      return decorateColumn(column, {
        key: columnKey,
        isFrozen: frozenLeafKeySet.has(columnKey),
        isLastFrozen: false,
      })
    })
    .filter(Boolean) as ReportColumn[]

  return packFrozenColumns(visibleColumns, frozenLeafOrder)
}

export const toggleColumnGroup = (
  currentHiddenKeys: string[],
  group: ColumnGroup,
  allLeafKeys: string[]
) => {
  const groupLeafKeys = group.leaves.map((leaf) => leaf.key)
  const visibleLeafKeys = allLeafKeys.filter(
    (key) => !currentHiddenKeys.includes(key)
  )
  const hasVisibleLeaf = groupLeafKeys.some((key) =>
    visibleLeafKeys.includes(key)
  )

  if (hasVisibleLeaf) {
    const nextHidden = Array.from(
      new Set([...currentHiddenKeys, ...groupLeafKeys])
    )
    const nextVisibleCount = allLeafKeys.filter(
      (key) => !nextHidden.includes(key)
    ).length

    return nextVisibleCount ? nextHidden : currentHiddenKeys
  }

  return currentHiddenKeys.filter((key) => !groupLeafKeys.includes(key))
}

export const toggleLeafColumn = (
  currentHiddenKeys: string[],
  leafKey: string,
  allLeafKeys: string[]
) => {
  if (currentHiddenKeys.includes(leafKey)) {
    return currentHiddenKeys.filter((key) => key !== leafKey)
  }

  const visibleLeafCount = allLeafKeys.filter(
    (key) => !currentHiddenKeys.includes(key)
  ).length

  if (visibleLeafCount <= 1) return currentHiddenKeys

  return [...currentHiddenKeys, leafKey]
}

export const resolveVirtualScrollX = (
  scrollX: number | string | true | undefined,
  columns: ReportColumn[]
) => {
  if (typeof scrollX === "number") return scrollX

  return flattenColumns(columns).reduce(
    (total, column) => total + Number(column.width || DEFAULT_COLUMN_WIDTH),
    0
  )
}

export const resolveVirtualScrollY = (
  scrollY: number | string | undefined,
  viewportHeight: number
) => {
  if (typeof scrollY === "number") return scrollY
  if (!scrollY || typeof scrollY !== "string") return DEFAULT_VIRTUAL_SCROLL_Y

  const calcMatch = scrollY.match(/^calc\(100vh\s*-\s*(\d+)px\)$/)
  if (calcMatch) {
    return Math.max(320, viewportHeight - Number(calcMatch[1]))
  }

  const pxMatch = scrollY.match(/^(\d+)px$/)
  if (pxMatch) return Number(pxMatch[1])

  return DEFAULT_VIRTUAL_SCROLL_Y
}

export const getColumnKey = (
  column: ReportColumn,
  index: number,
  parentKey?: string
) => {
  const dataIndex = Array.isArray(column.dataIndex)
    ? column.dataIndex.join(".")
    : column.dataIndex
  return String(column.key || dataIndex || `${parentKey || "column"}-${index}`)
}

export const getColumnTitle = (title: ReportColumn["title"]) => {
  if (typeof title === "string") return title
  if (typeof title === "number") return String(title)
  return "Column"
}

/**
 * The name a column is listed under in the freeze and visibility panels.
 *
 * Action columns are almost always declared with `title: ""`, which listed them
 * as a blank row: present, and checkable, but with nothing to read, so the
 * column looked as though it could not be frozen at all.
 */
const getColumnLabel = (column: ReportColumn) => {
  const title = getColumnTitle(column.title)
  if (!title.trim() && isActionColumn(column)) return ACTION_COLUMN_TITLE
  return title
}

export const flattenColumns = (columns: ReportColumn[]): ReportColumn[] =>
  columns.flatMap((column) =>
    column.children?.length ? flattenColumns(column.children) : [column]
  )

const packFrozenColumns = (
  columns: ReportColumn[],
  frozenLeafOrder = new Map<string, number>()
) => {
  const frozenColumns = columns.filter(isFrozenColumn).sort((a, b) => {
    const aKey = String(a.children?.[0]?.key || a.key || "")
    const bKey = String(b.children?.[0]?.key || b.key || "")
    return (
      (frozenLeafOrder.get(aKey) ?? Number.MAX_SAFE_INTEGER) -
      (frozenLeafOrder.get(bKey) ?? Number.MAX_SAFE_INTEGER)
    )
  })
  const scrollColumns = columns.filter((column) => !isFrozenColumn(column))

  return [...frozenColumns, ...scrollColumns].map((column, index) => {
    const isLastFrozen =
      index === frozenColumns.length - 1 && frozenColumns.length > 0

    return setFreezeDivider(column, isLastFrozen)
  })
}

const isFrozenColumn = (column: ReportColumn) =>
  Boolean(column.fixed) ||
  Boolean(
    column.children?.length && column.children.every((child) => child.fixed)
  )

const setFreezeDivider = (
  column: ReportColumn,
  isLastFrozen: boolean
): ReportColumn => {
  const className = [
    removeFreezeDividerClass(column.className),
    isLastFrozen ? "report-freeze-divider" : "",
  ]
    .filter(Boolean)
    .join(" ")

  if (!column.children?.length) {
    return {
      ...column,
      className,
    }
  }

  return {
    ...column,
    className,
    children: column.children.map((child, index) =>
      setFreezeDivider(
        child,
        isLastFrozen && index === column.children!.length - 1
      )
    ),
  }
}

const removeFreezeDividerClass = (className: ReportColumn["className"]) =>
  String(className || "")
    .split(" ")
    .filter((item) => item && item !== "report-freeze-divider")
    .join(" ")

const decorateColumn = (
  column: ReportColumn,
  options: {
    key: string
    isFrozen: boolean
    isLastFrozen: boolean
  }
): ReportColumn => {
  const className = [
    removeFreezeDividerClass(column.className),
    options.isLastFrozen ? "report-freeze-divider" : "",
  ]
    .filter(Boolean)
    .join(" ")

  return {
    ...column,
    key: options.key,
    fixed: options.isFrozen ? ("left" as const) : undefined,
    width: column.width || DEFAULT_COLUMN_WIDTH,
    className,
    shouldCellUpdate:
      column.shouldCellUpdate ||
      ((record, previousRecord) => record !== previousRecord),
  }
}
